"use client"

import { useState, useEffect } from "react"
import { motion } from "framer-motion"
import { FaSearch, FaFilter, FaTimes } from "react-icons/fa"
import LawFirmCard from "@/components/start/LawFirmCard"
import { getOrg } from "@/services/Lawyers/getOrg"

const LawFirmListings = () => {
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedSpecialty, setSelectedSpecialty] = useState("")
  const [selectedLocation, setSelectedLocation] = useState("")
  const [priceRange, setPriceRange] = useState([0, 4])
  const [sortBy, setSortBy] = useState("rating")
  const [lawFirms, setLawFirms] = useState([])
  const [filteredFirms, setFilteredFirms] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [specialties, setSpecialties] = useState([])
  const [locations, setLocations] = useState([])
  const [error, setError] = useState(null)

  // Obtener los bufetes de la API
  useEffect(() => {
    const fetchLawFirms = async () => {
      try {
        setIsLoading(true)
        const data = await getOrg()

        // Verificar si data es un array o si tiene una propiedad list
        const firms = Array.isArray(data) ? data : data.list || []

        if (firms.length === 0) {
          console.warn("No se encontraron bufetes en la respuesta")
        }

        setLawFirms(firms)

        // Extraer especialidades únicas de los servicios
        const allSpecialties = firms.flatMap((firm) => (firm.services ? firm.services.map((s) => s.service) : []))
        setSpecialties([...new Set(allSpecialties)])

        // Extraer ubicaciones únicas
        const allLocations = firms
          .filter((firm) => firm.ubication && firm.ubication.city)
          .map((firm) => `${firm.ubication.city}, ${firm.ubication.province || ""}`)
        setLocations([...new Set(allLocations)])
      } catch (error) {
        console.error("Error al obtener los bufetes:", error)
        setError("No se pudieron cargar los bufetes. Por favor, intenta de nuevo más tarde.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchLawFirms()
  }, [])

  // Filtrar los bufetes según los criterios de búsqueda
  useEffect(() => {
    if (!lawFirms.length) {
      setFilteredFirms([])
      return
    }

    let filtered = [...lawFirms]

    // Aplicar filtro de búsqueda
    if (searchTerm) {
      filtered = filtered.filter(
        (firm) =>
          firm.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          firm.about_us?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (firm.services && firm.services.some((s) => s.service.toLowerCase().includes(searchTerm.toLowerCase()))),
      )
    }

    // Aplicar filtro de especialidad
    if (selectedSpecialty) {
      filtered = filtered.filter((firm) => firm.services && firm.services.some((s) => s.service === selectedSpecialty))
    }

    // Aplicar filtro de ubicación
    if (selectedLocation) {
      filtered = filtered.filter(
        (firm) => firm.ubication && `${firm.ubication.city}, ${firm.ubication.province || ""}` === selectedLocation,
      )
    }

    // Aplicar ordenamiento
    filtered.sort((a, b) => {
      switch (sortBy) {
        case "rating":
          // Ordenar por número de clientes como aproximación a la valoración
          return (b.clients?.length || 0) - (a.clients?.length || 0)
        case "reviews":
          return (b.clients?.length || 0) - (a.clients?.length || 0)
        case "price-asc":
          // Ordenar por precio promedio de servicios
          const avgPriceA = a.services?.reduce((sum, s) => sum + s.price, 0) / (a.services?.length || 1) || 0
          const avgPriceB = b.services?.reduce((sum, s) => sum + s.price, 0) / (b.services?.length || 1) || 0
          return avgPriceA - avgPriceB
        case "price-desc":
          const avgPriceADesc = a.services?.reduce((sum, s) => sum + s.price, 0) / (a.services?.length || 1) || 0
          const avgPriceBDesc = b.services?.reduce((sum, s) => sum + s.price, 0) / (b.services?.length || 1) || 0
          return avgPriceBDesc - avgPriceADesc
        default:
          return 0
      }
    })

    setFilteredFirms(filtered)
  }, [lawFirms, searchTerm, selectedSpecialty, selectedLocation, sortBy])

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
        <h1 className="text-4xl font-bold mb-4">Bufetes de Abogados</h1>
        <p className="text-gray-600">Encuentra el bufete perfecto para tus necesidades legales</p>
      </motion.div>

      <div className="flex flex-col md:flex-row gap-4 mb-8">
        <div className="flex-1">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar bufetes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
          >
            <option value="rating">Mejor valorados</option>
            <option value="reviews">Más reseñas</option>
            <option value="price-asc">Precio: Menor a Mayor</option>
            <option value="price-desc">Precio: Mayor a Menor</option>
          </select>

          <button
            onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[var(--gold)] flex items-center gap-2"
          >
            <FaFilter />
            Filtros
          </button>
        </div>
      </div>

      {isFilterMenuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          className="mb-8 p-6 bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          <div className="grid md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2">Especialidad</label>
              <select
                value={selectedSpecialty}
                onChange={(e) => setSelectedSpecialty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              >
                <option value="">Todas</option>
                {specialties.map((specialty, index) => (
                  <option key={index} value={specialty}>
                    {specialty}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Ubicación</label>
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--gold)]"
              >
                <option value="">Todas</option>
                {locations.map((location, index) => (
                  <option key={index} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Rango de precios</label>
              <input
                type="range"
                min="0"
                max="4"
                step="1"
                value={priceRange[1]}
                onChange={(e) => setPriceRange([0, Number.parseInt(e.target.value)])}
                className="w-full"
              />
              <div className="flex justify-between mt-2 text-sm text-gray-500">
                <span>$</span>
                <span>$$$$</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Filtros activos */}
      <div className="flex flex-wrap gap-2 mb-6">
        {selectedSpecialty && (
          <span className="px-3 py-1 bg-gray-100 rounded-full flex items-center gap-2">
            {selectedSpecialty}
            <button onClick={() => setSelectedSpecialty("")} className="hover:text-red-500">
              <FaTimes />
            </button>
          </span>
        )}
        {selectedLocation && (
          <span className="px-3 py-1 bg-gray-100 rounded-full flex items-center gap-2">
            {selectedLocation}
            <button onClick={() => setSelectedLocation("")} className="hover:text-red-500">
              <FaTimes />
            </button>
          </span>
        )}
        {(priceRange[0] !== 0 || priceRange[1] !== 4) && (
          <span className="px-3 py-1 bg-gray-100 rounded-full flex items-center gap-2">
            {`$`.repeat(priceRange[0])} - {`$`.repeat(priceRange[1])}
            <button onClick={() => setPriceRange([0, 4])} className="hover:text-red-500">
              <FaTimes />
            </button>
          </span>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">{error}</div>}

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
      >
        {isLoading ? (
          // Esqueletos de carga
          [...Array(6)].map((_, index) => (
            <div key={index} className="bg-gray-100 rounded-lg h-[400px] animate-pulse" />
          ))
        ) : filteredFirms.length > 0 ? (
          filteredFirms.map((firm) => <LawFirmCard key={firm._id || firm.id} firm={firm} />)
        ) : (
          <div className="col-span-full text-center py-8">
            <p className="text-gray-500">No se encontraron bufetes que coincidan con los criterios de búsqueda.</p>
          </div>
        )}
      </motion.div>
    </div>
  )
}

export default LawFirmListings

